import axios from 'axios';
import * as types from './ticketTypes';
import apiClient from '../../utils/apiConfig'; // Add this import at the top

// Action creator for fetching user tickets
export const fetchUserTickets = (filters = {}) => async (dispatch, getState) => {
  try {
    dispatch({ type: types.USER_TICKET_FETCH_REQUEST });
    
    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    if (!token) {
      throw new Error("Authentication required");
    }
    
    // Build query string from filters
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.limit) queryParams.append('limit', filters.limit);
    
    const response = await axios.get(
      `${process.env.REACT_APP_URL}/api/user/tickets?${queryParams.toString()}`,
      {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );
    
    dispatch({
      type: types.USER_TICKET_FETCH_SUCCESS,
      payload: {
        tickets: response.data.tickets,
        totalTickets: response.data.totalTickets,
        page: response.data.page,
        limit: response.data.limit,
        pages: response.data.pages
      }
    });
    
    return { success: true, tickets: response.data.tickets };
  } catch (error) {
    console.error("Error fetching user tickets:", error);
    
    dispatch({
      type: types.USER_TICKET_FETCH_FAILURE,
      payload: {
        error: error.response?.data?.message || error.message
      }
    });
    
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

// Action creator for getting user ticket details
export const getUserTicketDetails = (ticketId) => async (dispatch, getState) => {
  try {
    dispatch({ type: types.USER_TICKET_DETAIL_REQUEST });
    
    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    if (!token) {
      throw new Error("Authentication required");
    }
    
    const response = await axios.get(
      `${process.env.REACT_APP_URL}/api/user/tickets/${ticketId}`,
      {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );
    
    dispatch({
      type: types.USER_TICKET_DETAIL_SUCCESS,
      payload: {
        ticket: response.data.ticket
      }
    });
    
    return { success: true, ticket: response.data.ticket };
  } catch (error) {
    console.error("Error fetching ticket details:", error);
    
    dispatch({
      type: types.USER_TICKET_DETAIL_FAILURE,
      payload: {
        error: error.response?.data?.message || error.message
      }
    });
    
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

// Action creator for replying to a user ticket
export const userReplyToTicket = (ticketId, message) => async (dispatch, getState) => {
  try {
    dispatch({ type: types.USER_TICKET_REPLY_REQUEST });
    
    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    if (!token) {
      throw new Error("Authentication required");
    }
    
    const response = await axios.post(
      `${process.env.REACT_APP_URL}/api/user/tickets/${ticketId}/reply`,
      { message },
      {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );
    
    dispatch({
      type: types.USER_TICKET_REPLY_SUCCESS,
      payload: {
        ticket: response.data.ticket
      }
    });
    
    return { success: true, ticket: response.data.ticket };
  } catch (error) {
    console.error("Error replying to ticket:", error);
    
    dispatch({
      type: types.USER_TICKET_REPLY_FAILURE,
      payload: {
        error: error.response?.data?.message || error.message
      }
    });
    
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

// Action creator for submitting a new ticket
export const submitTicket = (ticketData) => async (dispatch, getState) => {  try {
    dispatch({ type: types.USER_TICKET_SUBMIT_REQUEST });
    
    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    if (!token) {
      throw new Error("Authentication required");
    }
    
    // Use environment variable for API URL
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:2401';
    const apiUrl = `${apiBaseUrl}/api/user/submit-ticket`;
    console.log(`Submitting ticket to: ${apiUrl}`, ticketData);
    
    const response = await axios.post(
      apiUrl,
      ticketData,
      {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );
    
    dispatch({
      type: types.USER_TICKET_SUBMIT_SUCCESS,
      payload: {
        ticketId: response.data.ticketId
      }
    });
    
    return { success: true, ticketId: response.data.ticketId };
  } catch (error) {
    console.error("Error submitting ticket:", error);
    
    dispatch({
      type: types.USER_TICKET_SUBMIT_FAILURE,
      payload: {
        error: error.response?.data?.message || error.message,
        fieldErrors: error.response?.data?.fieldErrors
      }
    });
    
    return { 
      success: false, 
      error: error.message,
      fieldErrors: error.response?.data?.fieldErrors
    };
  }
};

// Action creator for feedback submission
export const submitFeedback = (feedbackData) => async (dispatch, getState) => {
  try {
    dispatch({ type: types.USER_FEEDBACK_SUBMIT_REQUEST });
    
    const token = getState().auth?.token || localStorage.getItem("burrowtoken");
    if (!token) {
      throw new Error("Authentication required");
    }
    
    const response = await apiClient.post(
      `/api/user/submit-feedback`,
      feedbackData,
      {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );
    
    dispatch({
      type: types.USER_FEEDBACK_SUBMIT_SUCCESS,
      payload: {
        ticketId: response.data.ticketId
      }
    });
    
    return { success: true, ticketId: response.data.ticketId };
  } catch (error) {
    console.error("Error submitting feedback:", error);
    
    dispatch({
      type: types.USER_FEEDBACK_SUBMIT_FAILURE,
      payload: {
        error: error.response?.data?.message || error.message,
        fieldErrors: error.response?.data?.fieldErrors || {}
      }
    });
    
    return { 
      success: false, 
      error: error.response?.data?.message || error.message,
      fieldErrors: error.response?.data?.fieldErrors || {} 
    };
  }
};

// Guest / anonymous feedback — NO auth required, by design (feedback needs no account).
// Public rate-limited endpoint POST /api/guest-report (owner design, confirmed 9-11-26).
export const submitGuestFeedback = (feedbackData) => async (dispatch) => {
  dispatch({ type: types.USER_FEEDBACK_SUBMIT_REQUEST });
  try {
    const response = await apiClient.post('/api/guest-report', feedbackData, {
      headers: { 'Content-Type': 'application/json' }
    });
    dispatch({
      type: types.USER_FEEDBACK_SUBMIT_SUCCESS,
      payload: { ticketId: response.data.ticketId }
    });
    return { success: true, ticketId: response.data.ticketId, message: response.data.message };
  } catch (error) {
    console.error('Error submitting guest feedback:', error);
    dispatch({
      type: types.USER_FEEDBACK_SUBMIT_FAILURE,
      payload: {
        error: error.response?.data?.message || error.message,
        fieldErrors: error.response?.data?.fieldErrors || {}
      }
    });
    return {
      success: false,
      error: error.response?.data?.message || error.message,
      fieldErrors: error.response?.data?.fieldErrors || {}
    };
  }
};
